import { useCallback, useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { SetPostItem } from '../../components/SetPostItem'
import { SetDetailDialog, type SetDetailTab } from '../../components/SetDetailDialog'
import { Section } from '../../components/Section'
import { track } from '../../lib/analytics'
import type { SetFeed } from '../../hooks/useSetPosts'
import { useHistoryState } from '../../hooks/useHistoryState'
import type { Artist, Set as FestivalSet, SetPost, UserProfile } from '../../types'

const FEED_PREVIEW_LIMIT = 10

export function FeedSection({
  feed,
  artists,
  sets,
  userProfiles,
  currentUserId,
  hasSchedule,
  onNavigateToSet,
  eventId,
}: {
  feed: SetFeed
  artists: Record<string, Artist>
  sets: FestivalSet[]
  userProfiles: Record<string, UserProfile>
  currentUserId: string | null
  hasSchedule: boolean
  onNavigateToSet: (set: FestivalSet) => void
  eventId: string
}) {
  const [expanded, setExpanded] = useState(false)
  const posts = feed.allPosts

  // Fallback dialog state for when there's no schedule
  const [dialog, setDialog] = useHistoryState<{ artistId: string; tab: SetDetailTab } | null>('feedArtist', null)
  const dialogArtistId = dialog?.artistId ?? null

  const setArtistNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of sets) {
      const names = s.artistIds.map(id => artists[id]?.name).filter(Boolean)
      if (names.length > 0) map.set(s.id, names.join(', '))
    }
    return map
  }, [sets, artists])

  const handlePostTap = useCallback(
    (post: SetPost) => {
      if (hasSchedule) {
        const set =
          (post.setId && sets.find(s => s.id === post.setId)) ||
          (post.artistId && sets.find(s => s.artistIds.includes(post.artistId!)))
        if (set) {
          onNavigateToSet(set)
          track('social_feed_post_tap', { event_id: eventId, target: 'schedule' })
          return
        }
      }
      // Fallback: open the artist feed dialog
      const artistId = post.artistId ?? (post.setId ? sets.find(s => s.id === post.setId)?.artistIds[0] : undefined)
      if (artistId) {
        setDialog({ artistId, tab: 'feed' })
        track('social_feed_post_tap', { event_id: eventId, target: 'artist_dialog' })
      }
    },
    [hasSchedule, sets, onNavigateToSet, eventId, setDialog],
  )

  if (posts.length === 0) return null

  const visible = expanded ? posts : posts.slice(0, FEED_PREVIEW_LIMIT)
  const hasMore = posts.length > FEED_PREVIEW_LIMIT

  const dialogArtist = dialogArtistId ? artists[dialogArtistId] : null
  const dialogPosts = dialogArtistId ? (feed.postsByArtist.get(dialogArtistId) ?? []) : []

  return (
    <Section
      title={fbt('Feed', 'Section heading for the recent posts feed in the social tab')}
    >
      <div className="flex flex-col gap-3">
        {visible.map(post => {
          const contextLabel = post.artistId
            ? artists[post.artistId]?.name
            : post.setId
              ? setArtistNames.get(post.setId)
              : undefined

          return (
            <SetPostItem
              key={post.id}
              post={post}
              profile={userProfiles[post.authorId]}
              isAuthor={post.authorId === currentUserId}
              onDelete={() => feed.deletePost(post.id)}
              contextLabel={contextLabel}
              onClick={() => handlePostTap(post)}
            />
          )
        })}
      </div>

      {hasMore && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-3 text-sm text-primary hover:underline cursor-pointer"
        >
          {fbt('Show all posts', 'Button to expand the truncated feed in the social tab')}
        </button>
      )}

      {dialog && dialogArtistId && (
        <SetDetailDialog
          open={true}
          onOpenChange={open => { if (!open) setDialog(null) }}
          tab={dialog.tab}
          onTabChange={tab => setDialog({ ...dialog, tab })}
          name={dialogArtist?.name}
          artistId={dialogArtistId}
          interestedMemberIds={[]}
          goingMemberIds={[]}
          currentUserStatus={null}
          posts={dialogPosts}
          profiles={userProfiles}
          currentUserId={currentUserId}
          groups={feed.groups}
          isGuest={feed.isGuest}
          onRequireAuth={feed.onRequireAuth}
          onCreatePost={feed.createPost}
          onDeletePost={feed.deletePost}
        />
      )}
    </Section>
  )
}
